import {isEmpty} from 'lodash';
import React, {FC, useMemo, useState} from 'react';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {DropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemContent} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemIcon} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIcon';
import {SearchDropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/search/searchDropdown';
import {PopoverCoordinator} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {computeCustomFieldIconName} from '../../../../../helpers/customFieldViewHelpers';
import {CustomAttributeModel, CustomFieldModel} from '../../../../../models/api/customFieldModel';

interface AddCustomAttributePopoverProps {
  isDisabled?: boolean;
  customAttributes: ReadonlyArray<CustomAttributeModel>;
  unsavedCustomAttributes: ReadonlyArray<CustomAttributeModel>;
  customFields: ReadonlyArray<CustomFieldModel>;
  onAddCustomAttribute: (model: CustomFieldModel) => void;
  interactionId: string;
}

export const AddCustomAttributePopover: FC<AddCustomAttributePopoverProps> = (props) => {
  const {
    isDisabled,
    customFields,
    customAttributes,
    unsavedCustomAttributes,
    onAddCustomAttribute,
    interactionId,
  } = props;
  const [query, setQuery] = useState('');

  const displayedCustomFields = useMemo(
    () =>
      customFields.filter(
        (cf) =>
          !customAttributes.some((ca) => ca.customField.id === cf.id) &&
          !unsavedCustomAttributes.some((ca) => ca.customField.id === cf.id),
      ),
    [customFields, customAttributes, unsavedCustomAttributes],
  );

  const filteredData = useMemo(() => {
    const lowerCaseQuery = query.toLocaleLowerCase();

    return displayedCustomFields.filter((cf) => cf.name.toLocaleLowerCase().includes(lowerCaseQuery));
  }, [displayedCustomFields, query]);

  const shouldDisable = isDisabled || isEmpty(displayedCustomFields);

  return (
    <PopoverCoordinator
      isDisabled={shouldDisable}
      renderButton={() => (
        <Button
          variant={ButtonVariantsEnum.LINK}
          interactionId={`${interactionId}-add-custom-attribute`}
          isDisabled={shouldDisable}
        >
          <Trans id="add-custom-attribute-title">+ Add</Trans>
        </Button>
      )}
      renderModal={(popoverProps) => (
        <SearchDropdown
          headerTitle={<Trans id="add-custom-attribute-popover-title">Add custom field</Trans>}
          data={filteredData}
          width={260}
          renderRow={(item) => (
            <DropdownItem onSelect={() => onAddCustomAttribute(item)}>
              <DropdownItemIcon
                label={null}
                iconName={computeCustomFieldIconName(item.type, VisualSizesEnum.SMALL)}
                iconSize={16}
              />
              <DropdownItemContent>{item.name}</DropdownItemContent>
            </DropdownItem>
          )}
          query={query}
          onQueryChange={setQuery}
          {...popoverProps}
        />
      )}
    />
  );
};
